'use client';

import { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { aiModelsApi, LTXVideoResponse, LTXVideoOptionsResponse } from '@/lib/ai-models-api';

type GenerationMode = 't2v' | 'i2v';

export default function LTXVideoPage() {
  const [mode, setMode] = useState<GenerationMode>('t2v');
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [width, setWidth] = useState(832);
  const [height, setHeight] = useState(480);
  const [numFrames, setNumFrames] = useState(97);
  const [steps, setSteps] = useState(30);
  const [guidanceScale, setGuidanceScale] = useState(3.0);
  const [upscale, setUpscale] = useState(false);
  const [seed, setSeed] = useState<number | null>(null);
  const [promptExpanded, setPromptExpanded] = useState(false);

  const [options, setOptions] = useState<LTXVideoOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LTXVideoResponse | null>(null);

  useEffect(() => {
    aiModelsApi.ltxVideoOptions()
      .then(setOptions)
      .catch(console.error);
  }, []);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setSourceImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleResolutionChange = (resString: string) => {
    const [w, h] = resString.split('x').map(Number);
    setWidth(w);
    setHeight(h);
  };

  const handleFrameCountChange = (frames: number) => {
    setNumFrames(frames);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    if (mode === 'i2v' && !sourceImage) return;

    setLoading(true);
    setError(null);

    try {
      let response: LTXVideoResponse;

      if (mode === 't2v') {
        response = await aiModelsApi.ltxVideoT2V({
          prompt,
          negative_prompt: negativePrompt || undefined,
          width,
          height,
          num_frames: numFrames,
          num_inference_steps: steps,
          guidance_scale: guidanceScale,
          upscale,
          seed,
        });
      } else {
        response = await aiModelsApi.ltxVideoI2V(sourceImage!, {
          prompt,
          negative_prompt: negativePrompt || undefined,
          width,
          height,
          num_frames: numFrames,
          num_inference_steps: steps,
          guidance_scale: guidanceScale,
          upscale,
          seed,
        });
      }

      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">LTX-Video - High-Quality Video Generation</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Parameters */}
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Mode Selector */}
                <div>
                  <label className="block text-sm font-medium mb-2">Generation Mode</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMode('t2v')}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                        mode === 't2v'
                          ? 'bg-[var(--accent)] text-black'
                          : 'bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      Text to Video
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('i2v')}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                        mode === 'i2v'
                          ? 'bg-[var(--accent)] text-black'
                          : 'bg-[var(--bg-primary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      Image to Video
                    </button>
                  </div>
                </div>

                {/* Source Image Upload (I2V mode) */}
                {mode === 'i2v' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Source Image</label>
                    <div className="relative">
                      {imagePreview ? (
                        <div className="relative">
                          <img
                            src={imagePreview}
                            alt="Source"
                            className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setSourceImage(null);
                              setImagePreview(null);
                              setResult(null);
                            }}
                            className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                          <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                          </svg>
                          <span className="mt-2 text-sm text-[var(--text-muted)]">Upload source image to animate</span>
                          <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      First frame of the video will match this image
                    </p>
                  </div>
                )}

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {mode === 't2v' ? 'Prompt (300+ tokens recommended)' : 'Motion Description'}
                  </label>
                  <div className="relative">
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={mode === 't2v'
                        ? 'Describe the scene in detail: setting, lighting, atmosphere, motion, camera movement...'
                        : 'Describe the motion/action to animate the image...'}
                      rows={promptExpanded ? 16 : 4}
                      className="w-full px-3 py-2 pr-10 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                    />
                    <button
                      type="button"
                      onClick={() => setPromptExpanded(p => !p)}
                      className="absolute top-2 right-2 p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded transition-colors"
                      title={promptExpanded ? 'Collapse prompt' : 'Expand prompt'}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        {promptExpanded ? (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M14.25 9h4.5m-4.5 0V4.5m0 4.5l5.25-5.25M9 14.25v4.5m0-4.5H4.5m4.5 0L3.75 19.5M14.25 14.25v4.5m0-4.5h4.5m-4.5 0l5.25 5.25" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9M20.25 20.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Negative Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Negative Prompt</label>
                  <input
                    type="text"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder={options?.default_negative_prompt || 'What to avoid in generation...'}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    Leave empty to use default: {options?.default_negative_prompt}
                  </p>
                </div>

                {/* Resolution */}
                <div>
                  <label className="block text-sm font-medium mb-2">Resolution</label>
                  <select
                    value={`${width}x${height}`}
                    onChange={(e) => handleResolutionChange(e.target.value)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    {options?.resolutions.map((res) => (
                      <option key={`${res.width}x${res.height}`} value={`${res.width}x${res.height}`}>
                        {res.width}x{res.height} - {res.label}
                      </option>
                    )) || (
                      <>
                        <option value="512x512">512x512 - 1:1 Square</option>
                        <option value="640x480">640x480 - 4:3 Standard</option>
                        <option value="832x480">832x480 - 16:9 SD</option>
                        <option value="1024x576">1024x576 - 16:9 HD</option>
                        <option value="1216x704">1216x704 - 16:9 Full HD</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Frame Count */}
                <div>
                  <label className="block text-sm font-medium mb-2">Frame Count</label>
                  <select
                    value={numFrames}
                    onChange={(e) => handleFrameCountChange(parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    {options?.frame_counts.map((fc) => (
                      <option key={fc.frames} value={fc.frames}>
                        {fc.frames} frames - {fc.duration}
                      </option>
                    )) || (
                      <>
                        <option value="25">25 frames - ~1 second</option>
                        <option value="49">49 frames - ~2 seconds</option>
                        <option value="97">97 frames - ~4 seconds</option>
                        <option value="121">121 frames - ~5 seconds</option>
                        <option value="193">193 frames - ~8 seconds</option>
                        <option value="257">257 frames - ~10 seconds</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Steps */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Inference Steps: {steps}
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={steps}
                    onChange={(e) => setSteps(parseInt(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    30 recommended for best quality
                  </p>
                </div>

                {/* Guidance Scale */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Guidance Scale: {guidanceScale.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="1.0"
                    max="10.0"
                    step="0.1"
                    value={guidanceScale}
                    onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    3.0 recommended for best results
                  </p>
                </div>

                {/* Upscale Option */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="upscale"
                    checked={upscale}
                    onChange={(e) => setUpscale(e.target.checked)}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <label htmlFor="upscale" className="text-sm font-medium">
                    2x Latent Upscaling
                  </label>
                  <span className="text-xs text-[var(--text-muted)]">
                    (Higher resolution, slower)
                  </span>
                </div>

                {/* Advanced Settings */}
                <details className="bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg">
                  <summary className="cursor-pointer font-medium p-3">Advanced Settings</summary>
                  <div className="p-3 pt-0 space-y-4">
                    {/* Seed */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Seed (optional)</label>
                      <input
                        type="number"
                        value={seed ?? ''}
                        onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="Random if empty"
                        className="w-full px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                      />
                    </div>
                  </div>
                </details>

                {/* Warning */}
                <div className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <strong>Note:</strong> LTX-Video 13B requires ~40GB+ VRAM and generation typically takes 2-5 minutes
                  depending on resolution and frame count. Enable upscaling for even higher quality at the cost of speed.
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!prompt.trim() || (mode === 'i2v' && !sourceImage) || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating Video...' : `Generate ${mode === 't2v' ? 'Text-to-Video' : 'Image-to-Video'}`}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results */}
            <div className="space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Generated Video</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">
                      {mode === 't2v' ? 'Enter a prompt to generate a video' : 'Upload an image and describe the motion'}
                    </p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Generating video...</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">This typically takes 2-5 minutes</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Generated Video */}
                    <div className="border border-[var(--border-subtle)] rounded-lg overflow-hidden">
                      <video
                        src={`data:video/mp4;base64,${result.video}`}
                        controls
                        autoPlay
                        loop
                        className="w-full"
                      />
                    </div>

                    {/* Download Button */}
                    <a
                      href={`data:video/mp4;base64,${result.video}`}
                      download="ltx_video_output.mp4"
                      className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                    >
                      Download Video
                    </a>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Resolution</p>
                        <p className="text-lg font-semibold">{result.width}x{result.height}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Frames</p>
                        <p className="text-lg font-semibold">{result.num_frames}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">FPS</p>
                        <p className="text-lg font-semibold">{result.fps}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Duration</p>
                        <p className="text-lg font-semibold">{result.duration_seconds.toFixed(1)}s</p>
                      </div>
                    </div>

                    {/* Additional Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Upscaled</p>
                        <p className="text-sm font-medium">{result.upscaled ? 'Yes (2x)' : 'No'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Seed</p>
                        <p className="text-sm font-medium">{result.seed ?? 'Random'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Mode</p>
                        <p className="text-sm font-medium">{mode.toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                        <p className="text-sm font-medium">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                      </div>
                    </div>

                    {/* Prompt Used */}
                    <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                      <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Prompt Used</p>
                      <p className="text-sm">{result.prompt}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
